import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  IconButton,
  Input,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiMoreVertical } from "react-icons/fi";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchAllCustomersList } from "../../../actions/management.action";

const SearchTh = ({ value, onChange }) => (
  <Th>
    <Input
      value={value}
      onChange={onChange}
      placeholder="Search"
      size="xs"
      bg="white"
    />
  </Th>
);

const Customers = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10,
  });
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    mobile: "",
    email: "",
    customerID: "",
    accountCategory: "",
    type: "",
    status: "",
  });

  const getCustomersList = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const res = await fetchAllCustomersList({ page, limit });
      const rows = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      const apiPagination = res?.pagination || res?.data?.pagination || {};
      const total = apiPagination.total ?? rows.length;
      const currentPage = apiPagination.currentPage ?? page;
      const pageLimit = apiPagination.limit ?? limit;
      const totalPages =
        apiPagination.totalPages ?? Math.max(1, Math.ceil(total / pageLimit));

      setCustomers(rows);
      setPagination({
        total,
        currentPage,
        totalPages,
        limit: pageLimit,
      });
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomersList(pagination.currentPage, pagination.limit);
  }, [pagination.currentPage, pagination.limit]);

  const filteredCustomers = customers.filter((customer) => {
    return (
      (customer?.name || "")
        .toLowerCase()
        .includes(columnFilters.name.toLowerCase()) &&
      (customer?.mobile || "")
        .toLowerCase()
        .includes(columnFilters.mobile.toLowerCase()) &&
      (customer?.email || "")
        .toLowerCase()
        .includes(columnFilters.email.toLowerCase()) &&
      (customer?.customerID || "")
        .toLowerCase()
        .includes(columnFilters.customerID.toLowerCase()) &&
      (customer?.accountCategory || "")
        .toLowerCase()
        .includes(columnFilters.accountCategory.toLowerCase()) &&
      (customer?.type || "")
        .toLowerCase()
        .includes(columnFilters.type.toLowerCase()) &&
      (customer?.status || "")
        .toLowerCase()
        .includes(columnFilters.status.toLowerCase())
    );
  });

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const showingCount = filteredCustomers.length;

  const startPage = Math.max(1, pagination.currentPage - 2);
  const endPage = Math.min(pagination.totalPages, startPage + 4);
  const adjustedStart = Math.max(1, endPage - 4);

  const pagesToShow = [];
  for (let i = adjustedStart; i <= endPage; i += 1) {
    pagesToShow.push(i);
  }

  return (
    <Box px={4} py={6}>
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
      >
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/customer", title: "Customers", on: true },
          ]}
        />

        <Flex gap={2} mt={{ base: 4, md: 0 }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowColumnSearch((prev) => !prev)}
          >
            {showColumnSearch ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button
            as={Link}
            to={"/management/customer/create"}
            colorScheme="blue"
            size="sm"
            borderRadius={5}
            px={6}
          >
            Add Customer
          </Button>
        </Flex>
      </Flex>

      <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200" overflowX="auto">
        <Table size="sm" bg="white" fontSize="small">
          <Thead>
            <Tr>
              <Th>S.No</Th>
              <Th w="220px" maxW="220px">Name</Th>
              <Th>Mobile No.</Th>
              <Th>Email</Th>
              <Th>Customer ID</Th>
              <Th>Category</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Rating</Th>
              <Th>Created Date</Th>
              <Th>Action</Th>
            </Tr>
            {showColumnSearch && (
              <Tr bg="gray.50">
                <Th></Th>
                <SearchTh
                  value={columnFilters.name}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, name: e.target.value })
                  }
                />
                <SearchTh
                  value={columnFilters.mobile}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, mobile: e.target.value })
                  }
                />
                <SearchTh
                  value={columnFilters.email}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, email: e.target.value })
                  }
                />
                <SearchTh
                  value={columnFilters.customerID}
                  onChange={(e) =>
                    setColumnFilters({
                      ...columnFilters,
                      customerID: e.target.value,
                    })
                  }
                />
                <SearchTh
                  value={columnFilters.accountCategory}
                  onChange={(e) =>
                    setColumnFilters({
                      ...columnFilters,
                      accountCategory: e.target.value,
                    })
                  }
                />
                <SearchTh
                  value={columnFilters.type}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, type: e.target.value })
                  }
                />
                <SearchTh
                  value={columnFilters.status}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, status: e.target.value })
                  }
                />
                <Th></Th>
                <Th></Th>
                <Th></Th>
              </Tr>
            )}
          </Thead>

          <Tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Tr key={i}>
                  <Td colSpan={11}>
                    <Skeleton height="32px" borderRadius="md" />
                  </Td>
                </Tr>
              ))
            ) : filteredCustomers.length === 0 ? (
              <Tr>
                <Td colSpan={11}>
                  <Flex justify="center" p={6}>
                    <Text color="gray.500">No customers found</Text>
                  </Flex>
                </Td>
              </Tr>
            ) : (
              filteredCustomers.map((customer, index) => (
                <Tr
                  key={customer?._id || index}
                  _hover={{ bg: "gray.50" }}
                  cursor="pointer"
                  onClick={() => {
                    if (!customer?._id) return;
                    navigate(`/management/customer/${customer._id}`);
                  }}
                >
                  <Td>
                    {(pagination.currentPage - 1) * pagination.limit + index + 1}
                  </Td>
                  <Td maxW="220px" w="220px">
                    <Flex align="center" gap={2} maxW="220px">
                      <Avatar name={customer?.name} size="xs" bg="blue.500" color="white" />
                      <Text isTruncated title={customer?.name || "-"}>
                        {customer?.name || "-"}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>{customer?.mobile || "-"}</Td>
                  <Td>{customer?.email || "-"}</Td>
                  <Td>{customer?.customerID || "-"}</Td>
                  <Td>{customer?.accountCategory || "-"}</Td>
                  <Td>
                    <Badge colorScheme={customer?.isCorporate ? "purple" : "blue"}>
                      {customer?.type || "-"}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={customer?.status === "Active" ? "green" : "red"}>
                      {customer?.status || "Unknown"}
                    </Badge>
                  </Td>
                  <Td>{customer?.rating ? `${customer.rating} / 5` : "-"}</Td>
                  <Td>{formatDate(customer?.createdAt)}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        aria-label="Customer actions"
                      />
                      <MenuList>
                        <MenuItem
                          as={Link}
                          to={`/management/customer/edit/${customer?._id}`}
                        >
                          Edit
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Flex justifyContent="center" alignItems="center" m={4} gap={2} flexWrap="wrap">
          <Flex align="center" gap={4} wrap="wrap" justify="center">
            <Text fontSize="sm" color="#8B909A">
              Showing
            </Text>
            <Select
              size="sm"
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setPagination((prev) => ({
                  ...prev,
                  currentPage: 1,
                  limit: newLimit,
                }));
              }}
              width="fit-content"
              bg="white"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Select>
            <Text fontSize="sm" color="#8B909A">
              {showingCount} of {pagination.total}
            </Text>
          </Flex>

          <Flex alignItems="center" gap={2} ml={10}>
            <Button
              size="sm"
              isDisabled={pagination.currentPage === 1 || loading}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: Math.max(1, prev.currentPage - 1),
                }))
              }
            >
              <GoChevronLeft />
            </Button>

            {pagesToShow.map((pageNum) => (
              <Button
                key={pageNum}
                size="sm"
                variant={pageNum === pagination.currentPage ? "solid" : "ghost"}
                bgColor={pageNum === pagination.currentPage ? "#0F60FF" : "#F1F2F6"}
                color={pageNum === pagination.currentPage ? "#FFFFFF" : "#8B909A"}
                _hover={{
                  bg: pageNum === pagination.currentPage ? "#0F60FF" : "#E7EAF0",
                }}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: pageNum,
                  }))
                }
                isDisabled={loading}
              >
                {pageNum}
              </Button>
            ))}

            <Button
              size="sm"
              isDisabled={
                pagination.currentPage >= pagination.totalPages || loading
              }
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: Math.min(prev.totalPages, prev.currentPage + 1),
                }))
              }
            >
              <GoChevronRight />
            </Button>
          </Flex>
        </Flex>
      </Box>


    </Box>
  );
};

export default Customers;
